import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Spacing } from '@/shared/theme';
import { Button, Title, Text, CenteredColumn } from '@/shared/ui';
import { Modal } from '@/shared/Modal';
import { useAnalytics } from '@/hooks/useAnalytics';
import { VIEW_API_ERROR } from '@/consts/events';
import { SupportModal } from './SupportModal';

interface ApiErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  titleOverride?: string;
  subtitleOverride?: string;
}

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${Spacing.x2}px;
  width: 100%;
`;

const ApiErrorModal: React.FC<ApiErrorModalProps> = ({
  isOpen,
  onClose,
  titleOverride,
  subtitleOverride,
}) => {
  const { t } = useTranslation();
  const { trackPopupView } = useAnalytics();
  const [showSupportModal, setShowSupportModal] = useState(false);

  // trackPopupView, not trackView, in the deps — the effect body calls the
  // former. Both are identity-stable today and are byte-identical wrappers over
  // `track` (the old view_/popup_ prefixing is gone; the split survives only so
  // call sites can signal intent), but a deps list that doesn't match the body is
  // how a stale closure gets introduced later (PR #101 review).
  useEffect(() => {
    if (isOpen) {
      trackPopupView({ event: VIEW_API_ERROR });
    }
  }, [isOpen, trackPopupView]);

  const handleGoBack = () => {
    onClose();
  };

  const handleContactSupport = () => {
    setShowSupportModal(true);
  };

  const title = titleOverride || t('modals.api-error.title');
  const subtitle = subtitleOverride || t('modals.api-error.subtitle');

  return (
    <>
      <Modal
        open={isOpen}
        onClose={handleGoBack}
        ariaLabelledBy="api-error-modal-title"
        ariaDescribedBy="api-error-modal-description"
      >
        <ModalContent>
          <CenteredColumn>
            <Title size="h3" centered bold id="api-error-modal-title">
              {title}
            </Title>
          </CenteredColumn>
          <CenteredColumn>
            <Text size="p1" centered id="api-error-modal-description">
              {subtitle}
            </Text>
          </CenteredColumn>
          <CenteredColumn marginTop={Spacing.x2}>
            <Button
              size="default"
              variant="brand-primary"
              onClick={handleGoBack}
              text={t('modals.api-error.go-back')}
            />
          </CenteredColumn>
          <CenteredColumn>
            <Button
              size="default"
              variant="brand-tertiary"
              onClick={handleContactSupport}
              text={t('general.contact-support')}
            />
          </CenteredColumn>
        </ModalContent>
      </Modal>
      <SupportModal isOpen={showSupportModal} onClose={() => setShowSupportModal(false)} />
    </>
  );
};

export { ApiErrorModal };
